import { useMutation, gql, useApolloClient } from '@apollo/client'
import { Formik } from 'formik'
import { useState } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'

import { Button } from 'components/v2/Button'
import { Column } from '@10xLabs/react-web/components/Column'
import { ErrorMessage } from 'components/v2/ErrorMessage'
import Heading from 'components/v2/Heading'
import Modal from 'components/v2/Modal'
import { Row } from '@10xLabs/react-web/components/Row'
import { Select } from 'components/v2/Select'
import { Textarea } from 'components/v2/Textarea'
import { CALL_STATUS } from 'features/Bookings/components/CallStatus/constants'
import { callStatusMessages } from 'features/Bookings/components/CallStatus/messages'
import { NOTIFICATION_CHANNEL } from 'features/Bookings/components/NotificationChannel/constants'
import { notificationChannelMessages } from 'features/Bookings/components/NotificationChannel/messages'
import { resolvers as bookingsResolvers } from 'resolvers/bookings'
import { getUnprocessableErrors } from 'utils/error'
import { safeFormatMessage } from 'utils/intl'
import { schema } from './schema'

const messages = defineMessages({
  selectNotificationChannel: {
    id: 'bookings.registerSupportInteraction.notificationChannel.select',
    defaultMessage: 'Select notification channel'
  },
  selectCallStatus: {
    id: 'bookings.registerSupportInteraction.callStatus.select',
    defaultMessage: 'Select call status'
  },
  notesPlaceholder: {
    id: 'bookings.registerSupportInteraction.notes.placeholder',
    defaultMessage: 'Notes: 250 char max.'
  }
})

const REGISTER_BOOKING_SUPPORT_INTERACTION = gql`
  mutation RegisterBookingSupportInteraction(
    $bookingId: ID!
    $notificationChannel: notificationChannel
    $callStatus: callStatus
    $notes: String
  ) {
    registerBookingSupportInteraction(
      input: { id: $bookingId, notificationChannel: $notificationChannel, callStatus: $callStatus, notes: $notes }
    )
  }
`

const ADD_BOOKING_SUPPORT_INTERACTION = gql`
  mutation AddBookingSupportInteraction(
    $bookingId: ID!
    $notificationChannel: notificationChannel
    $callStatus: callStatus
    $notes: String
    $username: String
  ) {
    addBookingSupportInteraction(
      bookingId: $bookingId
      notificationChannel: $notificationChannel
      callStatus: $callStatus
      notes: $notes
      username: $username
    ) @client
  }
`

const NOTIFICATION_CHANNEL_LIST = [
  NOTIFICATION_CHANNEL.EMAIL,
  NOTIFICATION_CHANNEL.PHONE,
  NOTIFICATION_CHANNEL.WHATSAPP,
  NOTIFICATION_CHANNEL.SOCIAL_MEDIA
]

const CALL_STATUS_LIST = [
  CALL_STATUS.VOICEMAIL,
  CALL_STATUS.HUNG_UP,
  CALL_STATUS.WRONG_NUMBER,
  CALL_STATUS.BUSY_NUMBER,
  CALL_STATUS.UNAVAILABLE_NUMBER,
  CALL_STATUS.NON_EXISTENT_NUMBER,
  CALL_STATUS.ATTENDANCE_CONFIRMED,
  CALL_STATUS.CANCELLATION,
  CALL_STATUS.SERVICE_CHANGE
]

const hasAtLeastOneValue = (values: TSFixMe): TSFixMe => {
  return values.notificationChannel || values.callStatus || values.notes
}

export const RegisterSupportInteractionModal = ({ booking, closeModal, onCompleted }: TSFixMe): TSFixMe => {
  const intl = useIntl()

  const client = useApolloClient()
  // @ts-expect-error FIXME
  client.addResolvers(bookingsResolvers)

  const [supportInteraction, setSupportInteraction] = useState<TSFixMe>()

  const [addBookingSupportInteraction] = useMutation(ADD_BOOKING_SUPPORT_INTERACTION)
  const [registerBookingSupportInteraction, { loading, error }] = useMutation(REGISTER_BOOKING_SUPPORT_INTERACTION, {
    onCompleted() {
      if (onCompleted) {
        onCompleted()
      } else {
        addBookingSupportInteraction({
          variables: {
            bookingId: booking.id,
            username: '',
            notificationChannel: supportInteraction.notificationChannel,
            callStatus: supportInteraction.callStatus,
            notes: supportInteraction.notes
          }
        })
        closeModal()
      }
    }
  })

  const handleSubmit = (values: TSFixMe): TSFixMe => {
    if (hasAtLeastOneValue(values)) {
      setSupportInteraction({
        notificationChannel: values.notificationChannel || null,
        callStatus: values.callStatus || null,
        notes: values.notes || null
      })

      registerBookingSupportInteraction({
        variables: {
          bookingId: booking.id,
          notificationChannel: values.notificationChannel || null,
          callStatus: values.callStatus || null,
          notes: values.notes || null
        }
      })
    }
  }

  const initialValues = {
    notificationChannel: '',
    callStatus: '',
    notes: ''
  }

  const callStatusSorted = CALL_STATUS_LIST.sort((a, b): TSFixMe => {
    a = safeFormatMessage({ intl, messages: callStatusMessages, key: a })
    b = safeFormatMessage({ intl, messages: callStatusMessages, key: b })
    return a.localeCompare(b)
  })

  return (
    <Modal.Container large>
      <Modal.Header>
        <Heading.H5>
          <FormattedMessage
            id="bookings.registerSupportInteraction.title"
            defaultMessage="Interaction with passenger"
          />
        </Heading.H5>
      </Modal.Header>

      <Formik initialValues={initialValues} validationSchema={schema(intl)} onSubmit={handleSubmit}>
        {(props): TSFixMe => {
          const { values, handleChange, handleBlur, errors, touched } = props

          return (
            <form onSubmit={props.handleSubmit}>
              <Modal.Content>
                <Row>
                  <Column>
                    <Select
                      id="notificationChannel"
                      value={values.notificationChannel}
                      onChange={handleChange}
                      label={
                        <FormattedMessage
                          id="bookings.registerSupportInteraction.notificationChannel.label"
                          defaultMessage="Notification channel"
                        />
                      }
                    >
                      <option value="">{intl.formatMessage(messages.selectNotificationChannel)}</option>

                      {NOTIFICATION_CHANNEL_LIST.map((notificationChannel): TSFixMe => {
                        return (
                          <option value={notificationChannel} key={notificationChannel}>
                            {safeFormatMessage({
                              intl,
                              messages: notificationChannelMessages,
                              key: notificationChannel
                            })}
                          </option>
                        )
                      })}
                    </Select>
                  </Column>
                </Row>

                <Row>
                  <Column>
                    <Select
                      id="callStatus"
                      value={values.callStatus}
                      onChange={handleChange}
                      label={
                        <FormattedMessage
                          id="bookings.registerSupportInteraction.callStatus.label"
                          defaultMessage="Call status"
                        />
                      }
                    >
                      <option value="">{intl.formatMessage(messages.selectCallStatus)}</option>

                      {callStatusSorted.map((callStatus): TSFixMe => {
                        return (
                          <option value={callStatus} key={callStatus}>
                            {safeFormatMessage({ intl, messages: callStatusMessages, key: callStatus })}
                          </option>
                        )
                      })}
                    </Select>
                  </Column>
                </Row>

                <Row>
                  <Column>
                    <Textarea
                      id="notes"
                      value={values.notes}
                      placeholder={intl.formatMessage(messages.notesPlaceholder)}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      errors={errors.notes}
                      touched={touched.notes}
                      label={
                        <FormattedMessage id="bookings.registerSupportInteraction.notes.label" defaultMessage="Notes" />
                      }
                    />
                  </Column>
                </Row>
              </Modal.Content>

              <Modal.Footer>
                {error && (
                  <Modal.ErrorMessage>
                    <ErrorMessage errors={getUnprocessableErrors(error)}>
                      <FormattedMessage
                        id="bookings.registerSupportInteraction.error"
                        defaultMessage="There was an error while registering your notes"
                      />
                    </ErrorMessage>
                  </Modal.ErrorMessage>
                )}
                <Modal.Actions>
                  <Button kind="cancel" onClick={(): TSFixMe => closeModal()}>
                    <FormattedMessage id="dictionary.cancel" defaultMessage="Cancel" />
                  </Button>
                  <Button type="submit" kind="primary" loading={loading} disabled={!hasAtLeastOneValue(values)}>
                    <FormattedMessage id="bookings.registerSupportInteraction.submit" defaultMessage="Save" />
                  </Button>
                </Modal.Actions>
              </Modal.Footer>
            </form>
          )
        }}
      </Formik>
    </Modal.Container>
  )
}
